import { calculateCanvasImageDimensions } from "client/utils/calculateCanvasImageDimensions";
import { getRandomId } from "client/utils/getRandomId";
import { loadImage } from "client/utils/imageEdit/loadImage";
import { useCallback } from "react";
import { setMeme, setMemeInfo } from "store/features/memeGenerator/actions";
import { TextBox } from "store/features/memeGenerator/types";
import { getDefaultPaddingValues } from "store/features/memeGenerator/types/padding";
import { getDefaultTextBoxes } from "store/features/memeGenerator/utils/getDefaultTextBoxes";
import { getInitialTextBoxSettings } from "store/features/memeGenerator/utils/getInitialTextBoxSettings";
import { store, useAppDispatch } from "store/store";
import { MemeTemplate, MemeTemplateTextBox } from "types/meme";
import { useMemeEditor } from "../../MemeEditorContext";
import { useIsImageDark } from "./useIsImageDark";
import { decodeHtmlCharCodes } from "client/utils/decodeHtmlCharCodes";

export const useSelectMeme = () => {
    const { canvasContainerRef } = useMemeEditor();
    const dispatch = useAppDispatch();

    const { handleIsImageDark } = useIsImageDark();

    const handleSelectMeme = useCallback(
        async (meme: MemeTemplate) => {
            if (store.getState().memeGenerator.meme) {
                dispatch(setMeme(null));
                await new Promise((resolve) => setTimeout(resolve, 100));
            }
            const container = canvasContainerRef?.current || document.body.querySelector("#mg-canvas-container");
            if (!container) return;
            /*
             * This is done to handle extra small memes
             */
            if (meme.settings.height < 400) {
                const newWidth = 500;
                const newHeight = (meme.settings.height * newWidth) / meme.settings.width;
                const xRatio = newWidth / meme.settings.width;
                const yRatio = newHeight / meme.settings.height;
                meme.settings.width *= xRatio;
                meme.settings.height *= yRatio;
                meme.settings.textBoxes =
                    meme.settings.textBoxes?.map((textBox) => ({
                        ...textBox,
                        left: textBox.left * xRatio,
                        top: textBox.top * yRatio,
                        width: textBox.width * xRatio,
                        height: textBox.height * yRatio,
                    })) || [];
            }
            const { width: memeWidth, height: memeHeight } = calculateCanvasImageDimensions(
                meme.settings.width,
                meme.settings.height,
                container.getBoundingClientRect().width,
                container.getBoundingClientRect().height,
                false,
            );
            const xRatio = memeWidth / meme.settings.width;
            const yRatio = memeHeight / meme.settings.height;
            meme.slugKey = meme.slugKey.toLowerCase();
            dispatch(
                setMeme({
                    template: {
                        ...meme,
                        title: decodeHtmlCharCodes(meme.title),
                    },
                    textBoxes:
                        meme.settings.textBoxes.length > 0
                            ? meme.settings.textBoxes.map((textBox, index) => {
                                  return {
                                      ...getInitialTextBoxSettings(
                                          {
                                              rotation: textBox.rotation || 0,
                                              fontColor: textBox.fontColor,
                                              verticalAlign: textBox.verticalAlign,
                                              textAlign: textBox.textAlign,
                                              forceCaps: textBox.forceCaps,
                                              font: textBox.font,
                                              ...adjustTextBoxDimensions(textBox, { xRatio, yRatio, memeHeight }),
                                          },

                                          getRandomId(),
                                      ),
                                      zIndex: index,
                                  };
                              })
                            : (() => {
                                  const textBoxes = getDefaultTextBoxes(meme.settings, { xRatio, yRatio }).map(
                                      (textBox) => {
                                          let width = textBox.width;
                                          let height = textBox.height;
                                          let left = textBox.left;
                                          let top = textBox.top;
                                          if (left <= 10) {
                                              left = 10;
                                              width -= 10;
                                          }
                                          if (top <= 10) {
                                              top = 10;
                                              height -= 10;
                                          }
                                          if (Math.abs(top + height - memeHeight) <= 10) {
                                              height -= 10;
                                          }
                                          // to handle the rotate and drag icon being cut from the bottom
                                          if (Math.abs(top + height - memeHeight) <= 40) {
                                              height -= 40;
                                          }
                                          return {
                                              ...textBox,
                                              left,
                                              top,
                                              width,
                                              height,
                                          };
                                      },
                                  );
                                  handleIsImageDark(meme.thumbnailUrl, textBoxes);
                                  return textBoxes;
                              })(),
                    padding: {
                        ...getDefaultPaddingValues("#000"),
                        isApplied: false,
                    },
                    settings: {
                        image: {
                            width: memeWidth,
                            height: memeHeight,
                            left: 0,
                            top: 0,
                        },
                        parentContainerInitial: container.getBoundingClientRect(),
                        itemsContainer: {
                            width: memeWidth,
                            height: memeHeight,
                            left: (container.getBoundingClientRect().width - memeWidth) / 2,
                            top: (container.getBoundingClientRect().height - memeHeight) / 2,
                        },
                    },
                    isDownloading: false,
                    icons: [],
                }),
            );
            setTimeout(async () => {
                const url = meme.thumbnailUrl;
                const { image, error } = await loadImage(url, true);
                if (error) return;
                const ColorThief = (await import("colorthief")).default;
                dispatch(
                    setMemeInfo({
                        dominantColor: new ColorThief()
                            .getPalette(image, 1)
                            .map((c) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`)[0],
                    }),
                );
            });
        },
        [canvasContainerRef, dispatch],
    );

    return {
        handleSelectMeme,
    };
};

const adjustTextBoxDimensions = (
    textBox: MemeTemplateTextBox | TextBox,
    { xRatio, yRatio, memeHeight }: { xRatio: number; yRatio: number; memeHeight: number },
) => {
    let width = textBox.width * xRatio;
    let height = textBox.height * yRatio;
    let left = textBox.left * xRatio;
    let top = textBox.top * yRatio;
    if (left <= 10) {
        left = 10;
        width -= 10;
    }
    if (top <= 10) {
        top = 10;
        height -= 10;
    }
    if (Math.abs(top + height - memeHeight) <= 10) {
        height -= 10;
    }
    // to handle the rotate and drag icon being cut from the bottom
    if (Math.abs(top + height - memeHeight) <= 40) {
        height -= 40;
    }
    return {
        left,
        top,
        width,
        height,
    };
};
